'use client';

import type {
  FunctionComponent,
  MouseEvent,
  PropsWithChildren} from 'react';
import {
  createElement,
  memo,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import classNames from 'classnames';

import { Cross } from '@ui/uikit/components/icons/mono/Cross';

import { Backdrop } from '../../Backdrop';
import { Overlay, OverlaysProvider } from '../../Overlays';
import styles from './Modal.module.scss';

interface ModalProps {
  isOpen?: boolean;
  disableClosureViaOverlayClick?: boolean;
  overlays?: Array<string>;
  onClose?: () => unknown;
}

const Modal = memo(
  ({
    isOpen: isVisible,
    disableClosureViaOverlayClick,
    overlays,
    onClose,
    children,
  }: PropsWithChildren<ModalProps>) => {
    const contentRef = useRef<HTMLDivElement>(null);

    const handleOverlayClick = useCallback(() => {
      if (!disableClosureViaOverlayClick) {
        onClose?.();
      }
    }, [disableClosureViaOverlayClick, onClose]);

    const handleDismissViaCross = useCallback(
      (event: MouseEvent) => {
        event.stopPropagation();

        onClose?.();
      },
      [onClose],
    );

    const handleContentClick = useCallback((event: MouseEvent<HTMLDivElement>) => {
      if (event.target !== contentRef.current) {
        event.stopPropagation();
      }
    }, []);

    const nodeRef = useRef<HTMLDivElement>(null);

    return (
      <Overlay in={isVisible} layer="modals">
        <OverlaysProvider layers={overlays}>
          <Backdrop onClick={handleOverlayClick} ref={nodeRef}>
            <div className={classNames(styles['modal'])}>
              <div className={classNames(styles['header'])}>
                <Cross onClick={handleDismissViaCross} />
              </div>

              <div
                className={classNames(styles['content'])}
                onClick={handleContentClick}
                ref={contentRef}
              >
                {children}
              </div>
            </div>
          </Backdrop>
        </OverlaysProvider>
      </Overlay>
    );
  },
);

type ModalComponent<TModalProps extends ModalProps> = FunctionComponent<TModalProps>;

function useModal<TModalProps extends ModalProps>(
  modal: ModalComponent<TModalProps> = Modal,
  props?: Omit<ModalProps, 'isOpen'>,
) {
  type TOpenModalArguments = TModalProps extends ModalProps & infer T
    ? [keyof T] extends [never]
      ? []
      : [T?]
    : [];

  const [isOpen, setIsOpen] = useState(false);
  const [parameters, setParameters] = useState<TModalProps | null>(null);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  return useMemo(() => {
    return [
      parameters
        ? createElement(modal, { ...props, ...parameters, isOpen, onClose: handleClose })
        : null,
      {
        open: (...args: TOpenModalArguments) => {
          setParameters(args.at(0) ?? null);
          setIsOpen(true);
        },
        close: () => setIsOpen(false),
      },
    ] as const;
  }, [isOpen, parameters, modal, props, handleClose]);
}

export { Modal, type ModalProps, useModal };
